<template>
  <div :class="['conditional-values', {'options-closed': !optionsOpen}]">
    <span class="visible-with" @click.stop="optionsToggle">
      {{ getLang('visibleWith') }}
      <i class="fas fa-chevron-circle-down fa-sm"></i>
    </span>
    <transition name="fade" mode="out-in">
      <template v-if="conditionalDomain">
        <div v-if="optionsOpen" class="conditional-options" key="conditional-options">
          <div
            v-if="!moreThanOneActiveChild()"
            key="all-values"
            :class="['option', 'all-values', {'selected': isOptionSelected('*')}]"
            @click.stop="optionClicked('*')"
          >
            {{ getLang('allOptionValues') }} <span class="option-id">*</span>
          </div>
          <div
            v-for="option in conditionalDomain.selected"
            :key="option.id"
            :class="['option', {'selected': isOptionSelected(option.id)}]"
            @click.stop="optionClicked(option.id)"
          >
            {{ option.name[currentLanguage] }}
            <span class="option-id">{{ option.id }}</span>
          </div>
        </div>
        <div v-else class="resume-options" key="resume-options">
          {{ element.conditionalValues.length }} / {{ conditionalDomain.selected.length }}
        </div>
      </template>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'conditional-values',
  props: {
    element: Object,
    parent: Object,
    getLang: Function,
    conditionalDomain: Object,
    currentLanguage: String
  },
  data () {
    return {
      optionsOpen: true
    }
  },
  methods: {
    optionsToggle () {
      this.optionsOpen = !this.optionsOpen
      this.$emit('conditionalOptionsToggle', this.element.id, this.optionsOpen)
    },
    isOptionsOpen () {
      return this.optionsOpen
    },
    isOptionSelected (optionId) {
      if (this.element.conditionalValues) {
        return this.element.conditionalValues.some(element => element === optionId)
      } else {
        return false
      }
    },
    optionClicked (optionId) {
      const optionIndex = this.element.conditionalValues.findIndex(element => element === optionId)
      if (optionIndex !== -1) {
        this.element.conditionalValues.splice(optionIndex, 1)
      } else {
        if (this.parent.children) {
          if (optionId === '*') {
            this.element.conditionalValues = ['*']
          } else {
            this.parent.children.forEach(child => {
              if (child.conditionalValues) {
                const allValuesOptionIndex = child.conditionalValues.findIndex(element => element === '*')
                if (allValuesOptionIndex !== -1) {
                  child.conditionalValues.splice(allValuesOptionIndex, 1)
                }
                const childOptionIndex = child.conditionalValues.findIndex(element => element === optionId)
                if (childOptionIndex !== -1) {
                  child.conditionalValues.splice(childOptionIndex, 1)
                }
              }
            })
            this.element.conditionalValues.push(optionId)
          }
        }
      }
    },
    moreThanOneActiveChild () {
      if (this.parent.children) {
        const activeCount = this.parent.children.filter(element => element.active === true).length
        return activeCount > 1
      } else {
        return false
      }
    }
  },
  watch: {
    'conditionalDomain.selected' (newValue, oldValue) {
      if (this.element.conditionalValues) {
        this.element.conditionalValues = this.element.conditionalValues.filter(
          value =>
            value === '*' ||
            this.conditionalDomain.selected.some(item => item.id === value)
        )
      }
    },
    'parent.children': {
      handler (newVal, oldVal) {
        if (this.moreThanOneActiveChild()) {
          this.parent.children.forEach(child => {
            if (child.conditionalValues) {
              const childOptionIndex = child.conditionalValues.findIndex(element => element === '*')
              if (childOptionIndex !== -1) {
                child.conditionalValues.splice(childOptionIndex, 1)
              }
            }
          })
        }
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.conditional-values {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #feffc9;
  border: 1px dotted #bf830c;
  height: 73px;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  display: flex;
  gap: 5px;
  transition: all 0.4s ease;

  .visible-with,
  .resume-options {
    font-size: 13px;
    color: #404040;
    padding: 9px 2px 3px 15px;
    display: block;
    word-break: break-all;
    width: 105px;
    min-width: 105px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    i {
      font-size: 12.5px;
      margin-left: 2px;
      vertical-align: inherit;
      transition: all 0.3s ease;
    }
  }

  .resume-options {
    font-size: 14px;
  }

  .visible-with:hover {
    background-color: #3333330d;
  }

  .conditional-options {
    flex-grow: 1;
    overflow-y: auto;
    padding-top: 4px;

    .option {
      border: 1px dotted #cfcfcf;
      display: inline-block;
      margin: 0 6.25px 6.25px 0;
      padding: 2.5px 7px 2.5px 9px;
      font-size: 13px;
      color: #404040;
      font-family: 'Source Sans Pro';
      background-color: #ffffffdd;
      cursor: pointer;
      transition: all 0.2s ease;
      border-radius: 4px;
      opacity: 0.85;
      border: 1px solid #e0e0e0;

      &:hover {
        border: 1px solid #212c62c5 !important;
      }

      &.selected {
        background-color: #212c62c5 !important;
        border: 1px solid #212c62c5 !important;
        color: #fff;
        opacity: 1;

        .option-id {
          color: #404040;
        }
      }

      .option-id {
        background-color: #cfcfcf;
        display: inline-block;
        vertical-align: baseline;
        padding: 0 5px 0;
        margin-left: 4px;
        border-radius: 4px;
        color: #404040;
      }

      &.all-values {
        background-color: #e2e2e2;
        text-transform: uppercase;
        font-weight: 600;
        border: 1px solid #cfcfcf;
      }
    }

    &::-webkit-scrollbar {
      width: 12px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #888;
      border-radius: 10px;
      border: 3px solid #f1f1f1;
    }

    &::-webkit-scrollbar-thumb:hover {
      background-color: #555;
    }
  }

  &.options-closed {
    height: 40px;

    .visible-with {
      i {
        transform: scaleY(-1);
      }
    }
  }
}
</style>
